import { ref, computed, inject, watch } from 'vue'
import { defineStore } from 'pinia'

import { useStoreCart } from './cart';
import { useStoreRestaurant } from './restaurant';
import { useStoreTranslation } from './translation';

import { useOrder } from '@/composables/order';
import { useApi } from '@/plugins/api';
import asyncRef from '@/utils/asyncRef';

const LOGGER_TAG = 'order-store';

export const useStoreOrder = defineStore('order', () => {
  const api = useApi();
  const logger = inject('logger');

  const cStore = useStoreCart();
  const rStore = useStoreRestaurant();
  const tStore = useStoreTranslation();

  const order = ref(null);
  const deliveryTypes = useOrder();
  const { getMethodIcon } = useOrder();
  const category = ref(null);
  const canOrder = ref(new asyncRef(true));
  const deliveryMethods = ref([]);

  async function setDeliveryMethod(method) {
    try {
      let { data } = await api.post('/order/act/set_delivery', { ...method });
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
      return false;
    }
  }

  async function selectPos(value) {
    try {
      let response = await api.post('/order/act/set_pos', { pos_id: value.id });
      await getOrder();
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getDeliveryMethods() {
    try {
      let response = await api.get("/order/delivery_methods");
      let methods = response.data;

      for (let i = 0; i < methods.length; i++) {
        let method = methods[i];
        method.icon = await getMethodIcon(method);
        methods[i] = method;
      }

      deliveryMethods.value = methods;
      logger.info(LOGGER_TAG, 'deliveryMethods', deliveryMethods);
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  function selectCategory(value) {
    category.value = value;
    //persist('order.category', value);
  }

  async function getOrder() {
    try {
      let { data } = await api.get('/order');
      order.value = data.order;
      cStore.setItems(data.order.details);
      cStore.setTotal(data.order.total);
      cStore.setCoupon(data.order.coupon);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getCanOrder() {
    try {
      let { data } = await api.get('order/act/can_order');
      logger.info(LOGGER_TAG, 'can-order', data);
      canOrder.value.data = data;
      canOrder.value.loading = false;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
      canOrder.value.data = false;
      canOrder.value.loading = false;
    }
  }

  async function doAct(action, id = null, params = null) {
    try {
      logger.info(LOGGER_TAG, 'doAct', action, id, params);
      let { data } = await api.post(`order/act/${action}${id ? '/' + id : ''}`, params);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function canCheckout() {
    try {
      let { data } = await api.get('order/act/can_checkout');
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getTablebooking(posUid) {
    try {
      let { data } = await api.get('tablebooking', { params: { pos_uid: posUid } });
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getTablebookingAvailableTimes(posUid, date) {
    try {
      let { data } = await api.get('tablebooking/available_times', { params: { pos_uid: posUid, date } });
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function makeTablebookingReservation(formData) {
    try {
      let { data } = await api.post('tablebooking/send', formData);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }


  const deliveryMethod = computed(() => {
    if (!order) return null;
    if (deliveryMethods.value.length === 0) return null;
    if (order.value?._method === null || order.value?._method === undefined) return null;

    let method = deliveryMethods.value.find(m => m.code == order.value._method);
    return method;
  });

  const isDelivery = computed(() => {
    if ([deliveryTypes.CUSTOM_TYPE, deliveryTypes.COMPANY_TYPE].includes(deliveryMethod.value?.type)) {
      return deliveryMethod.value?.delivery === '1';
    }

    return [deliveryTypes.DELIVERY_TYPE, deliveryTypes.PICKUP_TYPE].includes(deliveryMethod.value?.type);
  });

  const orderTimeLabel = computed(() => {
    return isDelivery.value ? 'delivery' : 'pickup';
  });

  const isDeliveryWithAddress = computed(() => {
    if (deliveryMethod.value?.type == deliveryTypes.CUSTOM_TYPE) {
      return deliveryMethod.value?.delivery === '1' && deliveryMethod.value.address_is_standard;
    }

    return deliveryMethod.value?.type === deliveryTypes.DELIVERY_TYPE;
  });

  const hasDeliveryMethods = computed(() => {
    return deliveryMethods.value.length > 0;
  });

  const hasTableMethod = computed(() => {
    if (!hasDeliveryMethods.value) {
      return false;
    }

    return deliveryMethods.value.find((method) => {
      return method.type == deliveryTypes.TABLE_ORDER_TYPE;
    });
  });

  const deliveryFeeMin = computed(() => {
    if (!order?.value?.delivery_fee_summary?.order_min_errors) {
      return null;
    }

    let orderMin = order.value?.delivery_fee_summary.order_min_errors;
    if (orderMin.length === 0) {
      return null;
    }

    return orderMin[0];
  });

  const pos = computed(() => {
    if (!rStore.poss) {
      return null;
    }

    if (!order.value) {
      return null;
    }

    if (rStore.restaurant.has_subrestaurants && deliveryMethod.value?.type !== deliveryTypes.CUSTOM_TYPE) {
      if (!order.value._subrestaurant) {
        return null;
      }

      return rStore.poss[order.value?._subrestaurant?.id];
    }

    return rStore.poss[rStore.restaurant.id];
  });

  const deliveryPoint = computed(() => {
    return order.value?._ext_deliverypoint;
  });

  const isDigitalMenu = computed(() => {
    logger.info(LOGGER_TAG, 'isDigitalMenu', deliveryMethod, deliveryMethod.value);
    return deliveryMethod.value?.code == deliveryTypes.DIGITAL_MENU_TYPE;
  });

  const addressIsValid = computed(() => {
    // se è una delivery e address_is_standard è true allora controllo i campi dell'indirizzo
    let fields_to_check = ['address', 'city', 'zip', 'country', 'place_id', 'lat', 'lng'];

    if (!order.value) {
      return false;
    }

    let isValid = true;
    fields_to_check.forEach((field) => {
      if (order.value[field] == null || order.value[field] == '') {
        isValid = false;
      }
    });

    return isValid;
  });

  // filter event delivery methods
  const events = computed(() => {
    if (!hasDeliveryMethods.value) {
      return [];
    }

    return deliveryMethods.value.filter((method) => {
      return method.type == deliveryTypes.EVENT_TYPE;
    });
  });

  watch(() => deliveryMethod.value, (newMethod, oldMethod) => {
    logger.info(LOGGER_TAG, 'Delivery method changed:', newMethod, oldMethod);
    if (newMethod && newMethod.code !== oldMethod?.code) {
      tStore.getCustomLang();
    }
  });

  return {
    deliveryMethod, pos, category, canOrder, order, deliveryMethods, isDigitalMenu,
    deliveryFeeMin, deliveryPoint, orderTimeLabel, isDelivery, hasDeliveryMethods,
    hasTableMethod, isDeliveryWithAddress, addressIsValid, events,
    selectPos, selectCategory, getOrder, getCanOrder, getDeliveryMethods, setDeliveryMethod, doAct,
    canCheckout, getTablebooking, getTablebookingAvailableTimes, makeTablebookingReservation
  }
});
